"use client";

import { createContext, useContext, type ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useLabsIdentity, type LabsIdentity } from "./useLabsIdentity";

type LabsIdentityContextValue = {
  identity: LabsIdentity | null;
  loading: boolean;
};

const LabsIdentityContext = createContext<LabsIdentityContextValue>({
  identity: null,
  loading: true,
});

/** Resolves canonical Labs user (by email) once per session for all client pages. */
export function LabsIdentityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const workOsUser = user
    ? {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      }
    : null;
  const { identity, loading } = useLabsIdentity(workOsUser);
  return (
    <LabsIdentityContext.Provider value={{ identity, loading }}>
      {children}
    </LabsIdentityContext.Provider>
  );
}

export function useLabsIdentityContext() {
  return useContext(LabsIdentityContext);
}

/** Canonical Labs user id, API headers, and database profile (admin, access flags). */
export function useLabsApiAuth() {
  const { identity, loading } = useLabsIdentityContext();
  return {
    labsUserId: identity?.labsUserId ?? null,
    headers: identity?.headers ?? {},
    profile: identity?.profile ?? null,
    loading,
  };
}
